import {
  ArrowCounterClockwise,
  Broadcast,
  CalendarDots,
  ChatCircle,
  ClipboardText,
  Lightning,
  PhoneCall,
  Receipt,
  Robot,
  ShareNetwork,
  Signature,
  Sparkle,
  Star,
} from "@phosphor-icons/react"

import { cn } from "@/lib/utils"

const ICON_MAP: Record<
  string,
  React.ComponentType<{ className?: string; weight?: "fill" | "regular" }>
> = {
  PhoneCall,
  Lightning,
  Receipt,
  ChatCircle,
  Signature,
  Robot,
  Star,
  Broadcast,
  ArrowCounterClockwise,
  ShareNetwork,
  CalendarDots,
  ClipboardText,
  Sparkle,
}

const OUTCOME_GRADIENT: Record<string, string> = {
  "increase-sales": "from-blue-400 to-blue-600",
  "boost-productivity": "from-purple-400 to-purple-600",
  "improve-retention": "from-yellow-400 to-amber-500",
}

export function agentIconGradient(outcome: string | null | undefined): string {
  return outcome
    ? (OUTCOME_GRADIENT[outcome] ?? "from-zinc-400 to-zinc-600")
    : "from-zinc-400 to-zinc-600"
}

export function AgentGlyph({
  name,
  className = "size-5 text-white",
}: {
  name: string | null | undefined
  className?: string
}) {
  const Component = name ? (ICON_MAP[name] ?? Sparkle) : Sparkle
  return <Component className={className} weight="fill" />
}

// Single circular gradient swatch — a smaller, simpler variant of AgentAvatar
// for places that need just the gradient circle (e.g. the home explore
// carousel and agent cards). `size` controls the outer dimension; `iconSize`
// controls the inner glyph.
export function AgentBadge({
  icon,
  outcome,
  size = "size-10",
  iconSize = "size-5",
  className,
}: {
  icon: string | null | undefined
  outcome: string | null | undefined
  size?: string
  iconSize?: string
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br p-2",
        size,
        agentIconGradient(outcome),
        className,
      )}
    >
      <AgentGlyph name={icon} className={cn(iconSize, "text-white")} />
    </div>
  )
}

export function AgentAvatar({
  icon,
  outcome,
  className,
}: {
  icon: string | null | undefined
  outcome: string | null | undefined
  className?: string
}) {
  return (
    <div
      className={cn(
        "flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-900/5",
        className,
      )}
    >
      <div
        className={cn(
          "flex size-10 items-center justify-center rounded-full bg-gradient-to-br",
          agentIconGradient(outcome),
        )}
      >
        <AgentGlyph name={icon} />
      </div>
    </div>
  )
}
